'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { MapPin, BadgeCheck, Users, CalendarPlus, Star, Info, ExternalLink, CalendarClock, Guitar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GenerativeArtistAvatar } from '@/components/media/generative-artist-avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useRealPhoto } from '@/hooks/use-real-photo';
import type { BookingProfile } from '@/types/booking';

interface TalentCardProps {
  talent: BookingProfile;
  onBook?: (talent: BookingProfile) => void;
  compact?: boolean;
  /** Phones only: just the artwork (the 3-per-row marketplace grid). */
  thumbOnlyOnPhones?: boolean;
}

function formatRate(rates: BookingProfile['rates']): string | null {
  if (!rates) return null;
  if (rates.type === 'pwyw') return 'Name Your Price';
  if (rates.type === 'negotiable') return 'Rate negotiable';
  // fixed
  if (rates.perHour) return `from ${rates.perHour.toLocaleString()} ${rates.currency}/hr`;
  if (rates.perSession) return `from ${rates.perSession.toLocaleString()} ${rates.currency}/session`;
  if (rates.perGig) return `from ${rates.perGig.toLocaleString()} ${rates.currency}/gig`;
  if (rates.minimumRate) return `from ${rates.minimumRate.toLocaleString()} ${rates.currency}`;
  return null;
}

export function TalentCard({ talent, onBook, compact = false, thumbOnlyOnPhones = false }: TalentCardProps) {
  // Roles are collapsed to 3 by default; clicking "+N more" reveals all of
  // them (Gordon: "show everything ... or when one clicks to see all roles").
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const tc = useTranslations('common');
  const ti = useTranslations('instruments');
  const instruments = talent.instruments ?? [];
  // Placeholder pictures (initials, Google's default letter) → the BB vector art.
  const photo = useRealPhoto(talent.profileImageUrl);
  const visibleRoles = showAllRoles ? talent.bookingRoles : talent.bookingRoles.slice(0, 3);
  const extraRoles = talent.bookingRoles.length - visibleRoles.length;
  const visibleGenres = (talent.genres ?? []).slice(0, 2);
  const rateLabel = formatRate(talent.rates);

  return (
    <div
      className={cn(
        'bg-card border border-border rounded-lg flex flex-col gap-3 transition-shadow hover:shadow-md hover:shadow-black/20',
        compact ? 'p-3' : 'p-4',
        // 3 per row on a phone: the artwork alone, no squeezed text/buttons.
        thumbOnlyOnPhones && 'max-sm:p-0 max-sm:gap-0 max-sm:overflow-hidden max-sm:[&>*:not(:first-child)]:hidden'
      )}
    >
      {/* Square artwork thumbnail — clicks through to the talent's profile */}
      <Link
        href={`/profile/${talent.userId}`}
        className="relative block w-full aspect-square rounded-[var(--thumbnail-radius,0.5rem)] overflow-hidden bg-muted group"
        aria-label={tc('viewProfileOf', { name: talent.displayName })}
        title={tc('viewProfileOf', { name: talent.displayName })}
      >
        {photo ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={photo}
            alt={talent.displayName}
            className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          // No photo → the animated vector art with the name (not initials).
          <GenerativeArtistAvatar name={talent.displayName || '?'} className="absolute inset-0 h-full w-full" animate />
        )}
      </Link>

      {/* Name + location */}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-semibold text-foreground truncate text-sm">
            {talent.displayName}
          </span>
          {talent.verified && (
            <BadgeCheck className="h-4 w-4 text-blue-500 shrink-0" aria-label="Verified" />
          )}
        </div>

        {(talent.bookingRatingAvg || 0) > 0 && (
          <div className="flex items-center gap-1 text-xs mt-0.5">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400 shrink-0" />
            <span className="font-medium text-foreground">{talent.bookingRatingAvg}</span>
            <span className="text-muted-foreground">({talent.bookingRatingCount})</span>
          </div>
        )}

        {talent.bookingCity && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{talent.bookingCity}</span>
          </div>
        )}

        {talent.followerCount != null && talent.followerCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <Users className="h-3 w-3 shrink-0" />
            <span>{talent.followerCount.toLocaleString()} followers</span>
          </div>
        )}
      </div>

      {/* Booking roles */}
      {visibleRoles.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {visibleRoles.map(role => (
            <Badge key={role} variant="secondary" className="text-[10px] px-1.5 py-0">
              {role}
            </Badge>
          ))}
          {extraRoles > 0 && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setShowAllRoles(true); }}>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
                {tc('plusMore', { n: extraRoles })}
              </Badge>
            </button>
          )}
          {showAllRoles && talent.bookingRoles.length > 3 && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setShowAllRoles(false); }}>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 cursor-pointer hover:bg-muted transition-colors">
                Show less
              </Badge>
            </button>
          )}
        </div>
      )}

      {/* What they play — bookers look for an instrument first */}
      {instruments.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-foreground">
          <Guitar className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span><span className="sr-only">{ti('plays')} </span>{instruments.slice(0, 4).join(' · ')}{instruments.length > 4 ? ` ${tc('plusMore', { n: instruments.length - 4 })}` : ''}</span>
        </p>
      )}

      {/* Genres */}
      {visibleGenres.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {visibleGenres.map(genre => (
            <span
              key={genre}
              className="text-[10px] text-muted-foreground bg-muted/50 border border-border px-1.5 py-0 rounded-[var(--btn-radius)]"
            >
              {genre}
            </span>
          ))}
        </div>
      )}

      {/* Bio excerpt */}
      {!compact && talent.bookingBio && (
        <p className="text-xs text-muted-foreground line-clamp-2">{talent.bookingBio}</p>
      )}

      {/* Footer: rate + info + book buttons */}
      <div className="flex items-center justify-between gap-2 mt-auto pt-1">
        {rateLabel ? (
          <span className="text-xs text-muted-foreground">{rateLabel}</span>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => setInfoOpen(true)}
            aria-label={tc('aboutName', { name: talent.displayName })}
            title={tc('aboutName', { name: talent.displayName })}
          >
            <Info className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={() => onBook?.(talent)}
            aria-label={`Book ${talent.displayName}`}
          >
            <CalendarPlus className="h-3.5 w-3.5" />
            Book
          </Button>
        </div>
      </div>

      {/* Info dialog — the full talent rundown before booking */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {talent.displayName}
              {talent.verified && <BadgeCheck className="h-4 w-4 text-blue-500 shrink-0" aria-label="Verified" />}
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-4">
            <Link href={`/profile/${talent.userId}`} className="relative h-24 w-24 shrink-0 rounded-lg overflow-hidden bg-muted" aria-label={`View ${talent.displayName}'s profile`}>
              {photo ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={photo} alt={talent.displayName} className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <GenerativeArtistAvatar name={talent.displayName || '?'} className="absolute inset-0 h-full w-full" animate />
              )}
            </Link>
            <div className="min-w-0 space-y-1 text-xs text-muted-foreground">
              {(talent.bookingRatingAvg || 0) > 0 && (
                <div className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400 shrink-0" />
                  <span className="font-medium text-foreground">{talent.bookingRatingAvg}</span>
                  <span>({talent.bookingRatingCount} reviews)</span>
                </div>
              )}
              {talent.bookingCity && (
                <div className="flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" /><span>{talent.bookingCity}</span></div>
              )}
              {talent.followerCount != null && talent.followerCount > 0 && (
                <div className="flex items-center gap-1"><Users className="h-3 w-3 shrink-0" /><span>{talent.followerCount.toLocaleString()} followers</span></div>
              )}
              {talent.bookingAvailability && (
                <div className="flex items-center gap-1"><CalendarClock className="h-3 w-3 shrink-0" /><span>{talent.bookingAvailability}</span></div>
              )}
              {rateLabel && <p className="font-medium text-foreground">{rateLabel}</p>}
            </div>
          </div>
          {talent.bookingRoles.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {talent.bookingRoles.map(role => (
                <Badge key={role} variant="secondary" className="text-[10px] px-1.5 py-0">{role}</Badge>
              ))}
            </div>
          )}
          {instruments.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold">{ti('plays')}</p>
              <div className="flex flex-wrap gap-1">
                {instruments.map(name => (
                  <Badge key={name} variant="outline" className="text-[10px] px-1.5 py-0">{name}</Badge>
                ))}
              </div>
            </div>
          )}
          {(talent.genres ?? []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {(talent.genres ?? []).map(genre => (
                <span key={genre} className="text-[10px] text-muted-foreground bg-muted/50 border border-border px-1.5 py-0 rounded-[var(--btn-radius)]">{genre}</span>
              ))}
            </div>
          )}
          {talent.bookingBio && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{talent.bookingBio}</p>
          )}
          {talent.portfolioUrl && (
            <a href={talent.portfolioUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              <ExternalLink className="h-3 w-3" /> Portfolio
            </a>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button asChild variant="outline" size="sm" className="text-xs">
              <Link href={`/profile/${talent.userId}`}>View Profile</Link>
            </Button>
            <Button size="sm" className="text-xs" onClick={() => { setInfoOpen(false); onBook?.(talent); }}>
              <CalendarPlus className="h-3.5 w-3.5" /> Book
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * One talent as a row — the marketplace's Listing / List / Compact views
 * (Gordon 2026-11-01: the view buttons did nothing while talent always showed
 * as cards). Same photo, vector-art fallback and rate as the card.
 */
export function TalentRow({ talent, onBook, variant, bookLabel }: {
  talent: BookingProfile;
  onBook?: (talent: BookingProfile) => void;
  variant: 'listing' | 'list' | 'compact';
  bookLabel: string;
}) {
  const photo = useRealPhoto(talent.profileImageUrl);
  const rateLabel = formatRate(talent.rates);
  const details = [talent.bookingRoles.slice(0, 3).join(' · '), (talent.instruments ?? []).slice(0, 3).join(' · '), talent.bookingCity].filter(Boolean).join(' · ');
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      {variant !== 'compact' && (
        <Link
          href={`/profile/${talent.userId}`}
          className={cn('relative shrink-0 overflow-hidden rounded-[var(--thumbnail-radius,0.5rem)] bg-muted', variant === 'listing' ? 'h-24 w-24 sm:h-28 sm:w-28' : 'h-12 w-12')}
          aria-label={talent.displayName}
        >
          {photo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={photo} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
          ) : (
            <GenerativeArtistAvatar name={talent.displayName || '?'} className="absolute inset-0 h-full w-full" animate />
          )}
        </Link>
      )}
      <div className="min-w-0 flex-1">
        <Link href={`/profile/${talent.userId}`} className="block truncate text-sm font-semibold capitalize hover:text-primary">
          {talent.displayName}
        </Link>
        {details && <p className="truncate text-xs text-muted-foreground">{details}</p>}
        {variant === 'listing' && rateLabel && <p className="pt-0.5 text-xs font-medium">{rateLabel}</p>}
      </div>
      {variant !== 'listing' && rateLabel && <span className="hidden shrink-0 text-xs text-muted-foreground sm:block">{rateLabel}</span>}
      <Button size="sm" className="h-8 shrink-0 px-3 text-xs" onClick={() => onBook?.(talent)} aria-label={`${bookLabel} ${talent.displayName}`}>
        <CalendarPlus className="h-3.5 w-3.5" />
        {bookLabel}
      </Button>
    </div>
  );
}
